import {serviceAttemptOutcomeLabels} from './serviceAttemptOutcomes';
import {FormEvent, useEffect, useRef, useState} from 'react';
import {ApiError, correctProofDraft, createProofDraft, loadProofDrafts, ProofDraft, reviewProofDraft} from './api';

function recordedFacts(document: ProofDraft['document']) {
  const facts = document?.facts;
  if (!facts || typeof facts !== 'object' || Array.isArray(facts)) return null;
  const {order_id, attempt_id, outcome_code, evidence_manifest_hash} = facts as Record<string, unknown>;
  if (typeof order_id !== 'string' || !order_id || typeof attempt_id !== 'string' || !attempt_id
    || typeof outcome_code !== 'string' || !outcome_code || typeof evidence_manifest_hash !== 'string' || !evidence_manifest_hash) return null;
  return {order_id, attempt_id, outcome_code, evidence_manifest_hash};
}

export function ProofDraftReview({onChanged}: {onChanged?: () => void} = {}) {
  const [items, setItems] = useState<ProofDraft[] | null>(null);
  const [status, setStatus] = useState('');
  const [pending, setPending] = useState(true);
  const busy = useRef(true);
  const [blocked, setBlocked] = useState(false);
  const [correctionId, setCorrectionId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const reasonInput = useRef<HTMLTextAreaElement>(null);
  const [customer, setCustomer] = useState('');
  const [order, setOrder] = useState('');
  const [assignment, setAssignment] = useState('');
  const [attempt, setAttempt] = useState('');

  useEffect(() => {
    let active = true;
    loadProofDrafts().then(result => {if (active) setItems(result);})
      .catch(() => {if (active) setStatus('Proof drafts are not available. Reload to check again.');})
      .finally(() => {if (active) {busy.current = false; setPending(false);}});
    return () => {active = false;};
  }, []);

  async function reload() {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setItems(null);
    setStatus('Loading proof drafts…');
    setCorrectionId(null);
    try {
      setItems(await loadProofDrafts());
      setBlocked(false);
      setStatus('Latest proof drafts loaded. Check the current version before taking another action.');
    } catch {
      setBlocked(true);
      setStatus('Proof drafts are not available. Reload to check again.');
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  async function mutate(action: () => Promise<ProofDraft>, success: string) {
    if (busy.current || blocked || items === null) return;
    busy.current = true;
    setPending(true);
    setStatus('Saving proof draft…');
    let saved = false;
    try {
      await action();
      saved = true;
      onChanged?.();
      setCorrectionId(null);
      setItems(await loadProofDrafts());
      setStatus(success);
    } catch (error) {
      setBlocked(true);
      setCorrectionId(null);
      if (saved) setStatus('The change was saved, but the latest drafts could not be loaded. Reload to see the saved version.');
      else if (error instanceof ApiError && error.status === 403) setStatus('The proof draft request could not be completed. Reload to check availability.');
      else if (error instanceof ApiError && error.status === 409) setStatus('The proof draft changed. Reload and check the current version.');
      else setStatus('The result could not be confirmed. Reload and check the current version before taking another action.');
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  function create(event: FormEvent) {
    event.preventDefault();
    void mutate(() => createProofDraft({
      customer_organization_id: customer, order_id: order, assignment_id: assignment, attempt_id: attempt,
      expected_assignment_version: 2, expected_attempt_version: 1, legal_lock_sequence: 1,
      idempotency_key: crypto.randomUUID(),
    }), 'Synthetic proof draft prepared for human review. It is NOT FOR FILING.');
  }

  function correct(event: FormEvent, item: ProofDraft) {
    event.preventDefault();
    const trimmed = reason.trim();
    const bytes = new TextEncoder().encode(trimmed).length;
    if (bytes < 4 || bytes > 191) {
      setReasonError(bytes < 4 ? 'Enter a longer correction reason.' : 'Shorten the correction reason to fit the text limit.');
      reasonInput.current?.focus();
      return;
    }
    void mutate(() => correctProofDraft(item.proof_draft_id, item.draft_version, trimmed, crypto.randomUUID()),
      'Correction recorded as a new immutable draft version. It needs human review and remains NOT FOR FILING.');
  }

  const disabled = pending || blocked || items === null;
  return <section className="panel" aria-labelledby="proof-draft-heading">
    <p className="eyebrow">HUMAN-REVIEWED PREPARATION</p>
    <h2 id="proof-draft-heading">Synthetic proof drafts</h2>
    <p>Jurisdiction-neutral working drafts generated only from locked facts. Every version is marked <strong>NOT FOR FILING</strong>; no signature, notarization, or filing occurs here.</p>
    <form onSubmit={create}><fieldset disabled={disabled}>
      <legend>Prepare from exact locked lineage</legend>
      <label>Customer organization<input required pattern="[0-9a-f-]{36}" value={customer} onChange={e => setCustomer(e.target.value)}/></label>
      <label>Order<input required pattern="[0-9a-f-]{36}" value={order} onChange={e => setOrder(e.target.value)}/></label>
      <label>Assignment<input required pattern="[0-9a-f-]{36}" value={assignment} onChange={e => setAssignment(e.target.value)}/></label>
      <label>Finalized attempt<input required pattern="[0-9a-f-]{36}" value={attempt} onChange={e => setAttempt(e.target.value)}/></label>
      <button type="submit">Prepare synthetic draft</button>
    </fieldset></form>
    <p role="status" aria-live="polite" className="notice">{status || (items === null ? 'Loading proof drafts…' : items.length === 0 ? 'No proof draft has been prepared.' : '')}</p>
    <button type="button" disabled={pending} onClick={() => void reload()}>Reload proof drafts</button>
    {items && <ol className="intake-list">{items.map(item => {
      const facts = recordedFacts(item.document);
      const readable = facts !== null && facts.order_id === item.order_id && facts.attempt_id === item.attempt_id;
      const revision = item.document?.revision;
      const correctionReason = revision && typeof revision === 'object' && 'reason' in revision && typeof revision.reason === 'string' ? revision.reason : null;
      return <li className="intake-card" key={item.proof_draft_id}>
      <h3>Draft version {item.draft_version} · {item.state}</h3>
      <p className="notice"><strong>NOT FOR FILING</strong></p>
      {readable ? <>
        <p>Recorded facts from this draft. Review these before recording human review.</p>
        <dl aria-label="Recorded proof facts">
          <div><dt>Observed outcome</dt><dd>{Object.prototype.hasOwnProperty.call(serviceAttemptOutcomeLabels, facts.outcome_code) ? serviceAttemptOutcomeLabels[facts.outcome_code] : facts.outcome_code}</dd></div>
          <div><dt>Source order</dt><dd>{facts.order_id}</dd></div>
          <div><dt>Source attempt</dt><dd>{facts.attempt_id}</dd></div>
        </dl>
        <details><summary>Evidence and document references</summary>
          <dl><div><dt>Evidence manifest hash</dt><dd>{facts.evidence_manifest_hash}</dd></div>
          <div><dt>Draft document hash</dt><dd>{item.document_sha256}</dd></div></dl>
        </details>
      </> : <p className="notice error">Draft contents are unavailable or do not match this order and attempt. Reload proof drafts before recording human review.</p>}
      {correctionReason && <p>Correction reason: {correctionReason}</p>}
      {item.state === 'prepared' && <button type="button" disabled={disabled || correctionId !== null || !readable}
        onClick={() => void mutate(() => reviewProofDraft(item.proof_draft_id, item.draft_version, crypto.randomUUID()), 'Human review recorded as a new immutable draft version.')}>Record human review</button>}
      <button type="button" disabled={disabled || correctionId !== null} onClick={() => {
        setCorrectionId(item.proof_draft_id); setReason(''); setReasonError('');
      }}>Create correction</button>
      {correctionId === item.proof_draft_id && <form onSubmit={event => correct(event, item)}>
        <fieldset disabled={disabled}>
          <legend>Correct draft version {item.draft_version}</legend>
          <p>This records a reason in a new draft requiring human review. Locked source facts stay unchanged.</p>
          <label htmlFor="proof-correction-reason">Correction reason</label>
          <textarea id="proof-correction-reason" ref={reasonInput} required autoFocus value={reason}
            aria-invalid={Boolean(reasonError)} aria-describedby={reasonError ? 'proof-correction-error' : undefined}
            onChange={event => {setReason(event.target.value); setReasonError('');}}/>
          {reasonError && <p id="proof-correction-error" role="alert">{reasonError}</p>}
          <button type="submit">Save correction</button>
          <button type="button" onClick={() => setCorrectionId(null)}>Cancel correction</button>
        </fieldset>
      </form>}
    </li>;})}</ol>}
  </section>;
}
